// features/projects/templates/professional/simulator.js
// ═══════════════════════════════════════════════════════════
// PROFESSIONAL SIMULATOR — methodology workspace
//
// Six sub-tabs per docs/PROFESSIONAL_TEMPLATE_SPEC.md §VI:
//   Cases         ✓ live (Session 4 — lifted from lab-feature.js)
//   Hypotheses    ✓ live (Session 4 — lifted from lab-feature.js)
//   Records       ⏳ placeholder (graduates from Continuum testbed)
//   Phase A       ⏳ placeholder (graduates from Continuum testbed)
//   Benchmarks    ⏳ placeholder (graduates from Continuum testbed)
//   Track Record  ⏳ placeholder (graduates from Continuum testbed)
//
// Sub-tab persistence per workspace via localStorage. Disabled
// sub-tabs show their target session marker inline.
//
// The lifted Lab tabs query lab_* tables via /api/lab/* endpoints
// using `project.id`. For a Professional project to surface real
// data here, its workspace_id must match an existing lab_projects
// row — Session 5 will migrate lab_projects to projects with
// template='professional', after which the IDs align and Tim's
// existing seed becomes visible.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { L, FONT } from './tokens';
import { CasesTab, HypothesesTab } from '../../../lab-feature';

const SUB_TABS = [
  { key: 'cases',       label: 'Cases',        color: L.gold,
    description: 'Paired-case dossiers · click to expand',
    status: 'live' },
  { key: 'hypotheses',  label: 'Hypotheses',   color: L.gold,
    description: 'H-N registry with status + Phase A pills',
    status: 'live' },
  { key: 'records',     label: 'Records',      color: L.dim,
    description: 'Canonical records by truth-stage',
    status: 'pending', sessionMarker: 'graduates from Continuum' },
  { key: 'phase-a',     label: 'Phase A',      color: L.dim,
    description: 'Counter-Narrative · Outside View · Root Cause',
    status: 'pending', sessionMarker: 'graduates from Continuum' },
  { key: 'benchmarks',  label: 'Benchmarks',   color: L.dim,
    description: 'Pre-validation cases for composite indices',
    status: 'pending', sessionMarker: 'graduates when 2+ testbeds need it' },
  { key: 'track-record', label: 'Track Record', color: L.dim,
    description: 'Forward predictions · resolution dates · calibration',
    status: 'pending', sessionMarker: 'graduates from Continuum' },
];

export default function ProfessionalSimulator({ workspaceId, projectId, userId, supabase }) {
  const storageKey = `professional_simulator_subtab_${workspaceId}`;
  const [activeSubTab, setActiveSubTab] = useState(() => {
    if (typeof window === 'undefined') return 'cases';
    return window.localStorage.getItem(storageKey) || 'cases';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, activeSubTab);
  }, [storageKey, activeSubTab]);

  // Get the access token for the Lab API endpoints. Refresh on
  // each sub-tab activation so a long-open session doesn't fall
  // off a stale token.
  const [accessToken, setAccessToken] = useState(null);
  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!cancelled) setAccessToken(data?.session?.access_token || null);
      } catch {
        if (!cancelled) setAccessToken(null);
      }
    })();
    return () => { cancelled = true; };
  }, [supabase, activeSubTab]);

  // Lab API endpoints query by project_id (lab_projects.id). After
  // the Session 5 migration this aligns with projects.id. If projectId
  // isn't on the workspace_metadata yet (legacy workspaces), fall
  // back to workspaceId so the tabs still load — they'll just show
  // empty states until the project is properly migrated.
  const project = { id: projectId || workspaceId };

  return (
    <div style={{
      height: '100%', background: L.bg, color: L.text,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <SubTabNav active={activeSubTab} onSelect={setActiveSubTab} />
      <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'auto' }}>
        {activeSubTab === 'cases' && (
          <LiftedTabContainer>
            <CasesTab project={project} accessToken={accessToken} />
          </LiftedTabContainer>
        )}
        {activeSubTab === 'hypotheses' && (
          <LiftedTabContainer>
            <HypothesesTab project={project} accessToken={accessToken} />
          </LiftedTabContainer>
        )}
        {activeSubTab === 'records'      && <PendingPlaceholder tab={SUB_TABS[2]} />}
        {activeSubTab === 'phase-a'      && <PendingPlaceholder tab={SUB_TABS[3]} />}
        {activeSubTab === 'benchmarks'   && <PendingPlaceholder tab={SUB_TABS[4]} />}
        {activeSubTab === 'track-record' && <PendingPlaceholder tab={SUB_TABS[5]} />}
      </div>
    </div>
  );
}

// Sub-tab nav strip — same shape as Continuum's Record / Output
// tabs, with the Lab palette + a "live / pending" affordance per
// sub-tab so the user can see which sub-tabs are wired.
function SubTabNav({ active, onSelect }) {
  const activeMeta = SUB_TABS.find(t => t.key === active);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 36,
      borderBottom: `1px solid ${L.b1}`, background: L.s1, flexShrink: 0, gap: 14,
    }}>
      <div style={{
        fontFamily: FONT.mono, fontSize: 8, letterSpacing: '0.16em',
        color: L.dim, textTransform: 'uppercase', fontWeight: 600,
      }}>
        ◆ Simulator
      </div>
      <div style={{ width: 1, height: 14, background: L.b2 }} />
      <div style={{ display: 'flex', gap: 4 }}>
        {SUB_TABS.map(t => {
          const isActive = active === t.key;
          const isLive = t.status === 'live';
          return (
            <button
              key={t.key}
              onClick={() => onSelect(t.key)}
              title={t.description}
              style={{
                position: 'relative',
                fontSize: 9,
                fontFamily: FONT.mono,
                letterSpacing: '0.06em',
                padding: '4px 12px',
                borderRadius: 3,
                border: `1px solid ${isActive ? t.color : 'transparent'}`,
                background: isActive ? hexToBg(t.color, 0.10) : 'transparent',
                color: isActive ? t.color : (isLive ? L.static : L.dim),
                cursor: 'pointer',
                transition: 'all 0.12s',
                textTransform: 'uppercase',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.color = isLive ? L.mid : L.dim;
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.color = isLive ? L.static : L.dim;
              }}
            >
              {t.label}
              {!isLive && (
                <span style={{
                  marginLeft: 5, fontSize: 7, opacity: 0.7,
                  letterSpacing: '0.04em',
                }}>
                  ⏳
                </span>
              )}
            </button>
          );
        })}
      </div>
      {activeMeta && (
        <div style={{
          marginLeft: 'auto',
          fontFamily: FONT.sans, fontSize: 10, color: L.dim, fontStyle: 'italic',
        }}>
          {activeMeta.description}
        </div>
      )}
    </div>
  );
}

// The Lab tabs were built without a containing scroll wrapper; the
// Lab feature gave them their own page. Embedding them here just
// needs a thin padding/scroll container.
function LiftedTabContainer({ children }) {
  return (
    <div style={{ padding: '20px 24px', minHeight: '100%' }}>
      {children}
    </div>
  );
}

function PendingPlaceholder({ tab }) {
  return (
    <div style={{
      padding: '60px 24px',
      display: 'flex', justifyContent: 'center',
    }}>
      <div style={{ maxWidth: 560, width: '100%' }}>
        <div style={{
          fontFamily: FONT.mono, fontSize: 9, letterSpacing: '0.18em',
          color: L.dim, textTransform: 'uppercase', marginBottom: 8,
        }}>
          ⏳ {tab.label} · pending
        </div>
        <div style={{
          fontFamily: FONT.serif, fontSize: 22, fontWeight: 300,
          color: L.text, marginBottom: 14, lineHeight: 1.3,
        }}>
          {tab.description}
        </div>
        <div style={{
          fontFamily: FONT.sans, fontSize: 13, color: L.mid,
          lineHeight: 1.7, marginBottom: 16,
        }}>
          This sub-tab {tab.sessionMarker}. The pattern stays in the
          Continuum testbed until it proves out across multiple
          domains, then graduates here.
        </div>
        <div style={{
          padding: '12px 16px',
          background: L.s2, border: `1px solid ${L.b1}`, borderRadius: 5,
          fontSize: 11, color: L.dim, fontFamily: FONT.sans, lineHeight: 1.6,
          fontStyle: 'italic',
        }}>
          Per the build discipline (`docs/PROFESSIONAL_TEMPLATE_SPEC.md` §VIII):
          patterns must prove across 2+ testbeds before being added to
          the public Professional template. Honest absence beats
          premature feature surface.
        </div>
      </div>
    </div>
  );
}

function hexToBg(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
